<template>
  <div
    v-click-outside="close"
    :class="['ue-select position-relative', { 'opacity-75 pe-none cursor-not-allowed': disabled }]"
    @click="toggle"
    :aria-disabled="disabled"
  >
    <div
      class="ue-select__input d-flex align-items-center justify-content-between px-2 border rounded overflow-hidden text-truncate"
    >
      <span class="text-truncate" v-if="!value">{{ placeholder }}</span>
      <span class="text-truncate" v-else>{{ value[labelField] }}</span>
      <span class="ms-2"><DownArrowIcon /></span>
    </div>

    <ul
      v-if="isOpen"
      class="ue-select__options list-unstyled border mt-1 position-absolute bg-white w-100 shadow-sm"
      @wheel.stop
    >
      <li
        v-for="opt in options"
        :key="opt[keyField]"
        class="ue-select__option px-2 py-2 text-truncate"
        @click.stop="select(opt)"
      >
        <slot name="option" :option="opt">
          {{ opt[labelField] }}
        </slot>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import { PropType } from 'vue'
import clickOutside from '@/lib/directives/clickOutside'
import DownArrowIcon from '@app/graphics/DownArrowIcon.vue'

/**
 * Represents one selectable option.
 * @typedef {object} Option
 * @property {any} [key: string] - Additional fields representing option data.
 */
interface Option {
  [key: string]: any
}

/**
 * UeSelect — a lightweight dropdown component for single selection.
 *
 * Renders a list of Option objects, displays the current selection,
 * and emits the chosen item via v-model/input.
 * @example
 *
 * <UeSelect
 *   :options="users"
 *   v-model="selectedUser"
 *   labelField="username"
 *   keyField="id"
 *   placeholder="Select user"
 *   :disabled="isDisabled"
 *   @input="onUserSelected"
 * />
 */
export default Vue.extend({
  name: 'UeSelect',

  directives: { clickOutside },

  model: {
    prop: 'value',
    event: 'input',
  },

  components: { DownArrowIcon },

  props: {
    /**
     * Currently selected option (two-way bound via v-model).
     */
    value: {
      type: Object as PropType<Option | null>,
      default: null,
    },

    /**
     * List of option objects to display.
     */
    options: {
      type: Array as PropType<Option[]>,
      required: true,
    },

    /**
     * Key in each option object to display as label.
     */
    labelField: {
      type: String,
      default: 'label',
    },

    /**
     * Key in each option object to use as unique identifier.
     */
    keyField: {
      type: String,
      default: 'code',
    },

    /**
     * Text shown when no option is selected.
     */
    placeholder: {
      type: String,
      default: '',
    },

    /**
     * Prevents interaction when true.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isOpen: false,
    }
  },

  methods: {
    /**
     * Toggle the dropdown open state, unless disabled.
     */
    toggle(): void {
      if (!this.disabled) {
        this.isOpen = !this.isOpen
      }
    },

    /**
     * Close the dropdown list.
     */
    close(): void {
      this.isOpen = false
    },

    /**
     * Select an option, emit it via v-model/input, then close.
     * @param option — the Option object that was clicked
     */
    select(option: Option): void {
      if (!this.disabled && !option.isDisabled) {
        this.$emit('input', option)
        this.close()
      }
    },
  },
})
</script>

<style lang="sass" scoped>
.ue-select
  cursor: pointer
  user-select: none

  &__input
    height: 2.25rem

  &.cursor-not-allowed
    cursor: not-allowed

  &__options
    max-height: 200px
    overflow-y: auto
    z-index: 10
    overscroll-behavior: contain

  &__option
    cursor: pointer

    &:hover
      background-color: #f8f9fa
</style>
